import { Alert, Box, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Panel } from "../components/layout/Panel";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { JsonTree } from "../components/common/JsonTree";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { getHealthProbeRun, type HealthProbeRunDetail } from "../api/client";

export function PageServiceHealthDetail() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { responseId } = useParams<{ responseId: string }>();

  const [run, setRun] = useState<HealthProbeRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!responseId) {
      setLoading(false);
      return;
    }
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    getHealthProbeRun(responseId, abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        setRun(data);
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load probe result");
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [responseId]);

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  if (!responseId) {
    return (
      <Box>
        <PageErrorState message="Missing response ID in URL." />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
        <Panel title="Probe result">
          <PageLoadingState message="Loading probe result…" sx={{ mt: 1.5 }} />
        </Panel>
      </Box>
    );
  }

  if (error || !run) {
    return (
      <Box>
        <PageErrorState message={error ?? "Probe result not found."} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel
        title="Probe result"
        subtitle={`${run.service_name} · ${run.health_check_name} · ${new Date(run.observed_at).toLocaleString()}`}
      >
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Response ID: <span className="om-text-mono">{responseId}</span>
          </Typography>
          <Typography variant="body2">
            <b>Service:</b> {run.service_name} ({run.service_slug})
          </Typography>
          <Typography variant="body2">
            <b>Health check:</b> {run.health_check_name}
          </Typography>
          <Typography variant="body2">
            <b>Observed at:</b> {new Date(run.observed_at).toLocaleString()}
          </Typography>
        </Stack>
      </Panel>

      <Panel title="Probes (this run)">
        <Stack spacing={2}>
          {run.probes.map((probe) => (
            <Box
              key={probe.id}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {probe.probe_name}
                {probe.probe_unit ? ` (${probe.probe_unit})` : ""}
                {probe.probe_value != null ? `: ${probe.probe_value}` : ""}
              </Typography>
              {probe.probe_metadata != null && Object.keys(probe.probe_metadata as object).length > 0 ? (
                <JsonTree value={probe.probe_metadata} maxHeight={320} defaultExpandedDepth={2} />
              ) : null}
            </Box>
          ))}
        </Stack>
      </Panel>
    </Box>
  );
}
